import { findFilenames, findSyllableFilename } from './API';
import { escapeSsml, parseTextWithPhonemes } from './util';
import { aosTaskTypes } from '../constants/constants';
import { isSpanishAsrCheckStoryId } from '../constants/asrCheckStories';
import { logSessionInfo } from './logger';

/** Worst-case latency for an in-flight unit to notice an abort. */
export const ABORT_POLL_INTERVAL_MS = 100;

/**
 * Content language of a story, from its own tags — NOT getUserData().locale (the student's
 * interface/tutor locale, which can diverge from the assessment's content language for
 * bilingual students). AOS stamps the Spanish tag as "ES_MX"; other sources use "ES-MX" or
 * lowercase "es-mx", so match case- and separator-insensitively. This is the single source of
 * truth for the pre-recorded-audio content gate (Phrase, ModularMultipleChoice, Reader).
 */
export function getStoryContentLocale(story) {
  // GT-413: the Spanish ASR check carries no ES_MX tag, so only its storyId says it is Spanish.
  // Without this, en-us sessions served it would arm local English Vosk on a Spanish phrase.
  if (isSpanishAsrCheckStoryId(story?.storyId)) {
    return 'es-mx';
  }
  const tags = (story && story.tags) || [];
  const isSpanish = tags.some((t) => String(t).toUpperCase().replace(/-/g, '_') === 'ES_MX');
  return isSpanish ? 'es-mx' : 'en-us';
}

/**
 * Returns the story's content locale when a hydrated story is present, or
 * undefined when it is not. Unlike getStoryContentLocale (which returns 'en-us'
 * for absent/empty stories), returning undefined lets the ASR locale resolver's
 * fallback chain (tutorLocale -> session locale) operate as intended.
 */
export function getStoryContentLocaleOrNull(story) {
  return story ? getStoryContentLocale(story) : undefined;
}

/**
 * Evaluar (es-mx) markup instruction (blending + GPC): true when the story's CONTENT is
 * Spanish and its type carries <syllable>/<phoneme> markup (GPC) or a bare comma syllable
 * list (blending), and should therefore be voiced via playMarkupSequence() rather than raw
 * TTS. Gated on the story content locale, not the interface locale.
 */
export function isMarkupInstructionForStory(story) {
  return (
    getStoryContentLocale(story) === 'es-mx' &&
    (story?.type === aosTaskTypes.blending || story?.type === aosTaskTypes.graphemePhonemeConversion)
  );
}

/** Match Phrase intro segmentation for instruction repeat replay (after AOS pipe → [split] on LC). */
export function splitRepeatVariationOnSplitMarkers(raw) {
  const t = String(raw || '').trim();
  if (!t) return [];
  return t
    .split(/\[\s*split\s*\]/i)
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Fallback tokenizer for text with no <syllable>/<phoneme> markup (parseTextWithPhonemes
 * returned []). A blending repeatVariation is a bare comma syllable list ("ba, ñar") —
 * every segment a single whitespace-free token — and should be split so each syllable
 * resolves to its own pre-recorded audio. Some callers (e.g. the header repeat button)
 * can hand this function ordinary prose that happens to contain a comma (ReaderManager's
 * getRepeatInstructions() falls back to writtenInstructions/verbalInstructions when
 * repeatVariation is absent) — splitting THAT on commas would mis-voice comma-separated
 * multi-word phrases as isolated "syllables" (wrong pronunciation, wrong skipTranslation).
 * So only treat the comma-split as a syllable list when every segment is a single token;
 * otherwise speak the whole string as one ordinary (translating) text unit.
 */
function bareTextUnits(text) {
  const segments = splitRepeatVariationOnSplitMarkers(String(text || ''))
    .flatMap((seg) => String(seg).split(','))
    .map((t) => t.trim())
    .filter(Boolean);
  const looksLikeSyllableList = segments.length > 0 && segments.every((s) => !/\s/.test(s));
  if (looksLikeSyllableList) {
    return segments.map((value) => ({ type: 'syllable', value }));
  }
  const whole = String(text || '').trim();
  return whole ? [{ type: 'text', value: whole }] : [];
}

/**
 * Evaluar markup-instruction playback (blending + GPC): voice repeatVariation from
 * pre-recorded audio instead of sending raw <syllable>/<phoneme> markup to 11Labs
 * (which cannot voice the tags → silence). Shared by every caller that speaks a
 * repeat/idle text for a markup instruction — the initial instruction play, the
 * idle nudge, the instruction-phase overlay Replay, and the header repeat button
 * all need identical tokenizing/resolving so none of them silently regress.
 *  - A GPC repeatVariation is a full marked-up sentence → parseTextWithPhonemes splits it
 *    into text / syllable / phoneme units, played with the same resolvers as initial play.
 *  - A blending repeatVariation is a bare comma syllable list ("ba, ñar") with no markup →
 *    parseTextWithPhonemes returns [], so bareTextUnits() splits it into syllable tokens.
 *  - Any other untagged text (prose, possibly containing commas) is spoken as-is via TTS.
 *
 * @param {object} speech - the Speech API instance
 * @param {string} text - the repeat/idle text to voice
 * @param {object} [options]
 * @param {() => boolean} [options.isStale] - caller-supplied check (e.g. unmounted, or
 *   superseded by a newer replay) consulted before each unit; defaults to never-stale.
 * @param {string} [options.locale] - CONTENT locale of the story (from getStoryContentLocale).
 *   Passed to the phoneme/syllable resolvers so a bilingual session never leaks to the
 *   English asset set. Defaults to 'es-mx' — this player is only invoked for es-mx markup
 *   instructions — but callers should pass the story-derived locale explicitly.
 */
export async function playMarkupSequence(speech, text, { isStale, locale = 'es-mx' } = {}) {
  const checkStale = isStale || (() => false);

  // Capture the abort generation at sequence start. speakAudioFile (Howler) exposes no
  // abort callback and destroySound()'s onstop reports a clean finish, so a plain
  // speech.abortSpeech() (student taps an option mid-replay) would otherwise let the
  // loop cascade the remaining units over the next instruction/recording. abortSpeech
  // bumps this generation, so comparing against it is how we detect that abort.
  const startAbortGen = speech.getAbortSpeechGeneration?.();
  const isAborted = () =>
    checkStale() || speech.getAbortSpeechGeneration?.() !== startAbortGen;

  const parsed = parseTextWithPhonemes(String(text || ''));
  const units = parsed.length ? parsed : bareTextUnits(text); // GPC vs. blending/prose fallback

  // Speak text/fallback via TTS; resolves `true` if aborted mid-utterance. Text segments
  // translate per their own skipTranslation; an isolated syllable is a language-neutral
  // sound so it is never translated (even in bilingual mode).
  const speakViaTts = (value, skipTranslation) => new Promise((resolve) => {
    speech.speak({
      textToSpeak: String(value).startsWith('<speak>') ? value : `<speak>${escapeSsml(value)}</speak>`,
      passThroughSpeechTags: true,
      skipTranslation,
      onEnd: () => resolve(false),
      // Don't swallow a TTS failure: log it (no-silent-failure rule) but keep going.
      onError: (err) => {
        logSessionInfo('mmc_replay_tts_failed', { message: err?.message });
        resolve(false);
      },
      resolveAbort: () => resolve(true),
    });
  });

  // Play pre-recorded urls sequentially. `fallbackToken` (a syllable) is voiced via TTS
  // if its asset fails to LOAD (onfinish(false)) so it is never silent; phonemes pass
  // null (assets are always present, matching initial play). Resolves `true` if aborted.
  const playUrls = async (urls, fallbackToken) => {
    for (const url of urls) {
      if (isAborted()) return true;
      // eslint-disable-next-line no-await-in-loop
      const aborted = await new Promise((resolve) => {
        let settled = false;
        // destroySound() calls off() before stop(), tearing down the listener that fires onfinish,
        // so an abort mid-syllable would otherwise leave this await hanging — stranding
        // idleSpeechPlaying=true so the idle auto-submit timer never arms. Hence the poll.
        // Declared before finish() so the two do not depend on statement order: nothing calls
        // finish() before the interval is armed today, but moving the speakAudioFile call above
        // it would otherwise be a TDZ crash rather than a no-op.
        let abortPoll = null;
        const finish = (val) => {
          if (settled) return;
          settled = true;
          if (abortPoll) clearInterval(abortPoll);
          resolve(val);
        };
        abortPoll = setInterval(() => { if (isAborted()) finish(true); }, ABORT_POLL_INTERVAL_MS);
        let loadFailed = false;
        try {
          speech.speakAudioFile({
            url,
            // Fires before onfinish when the asset fails to load (missing/corrupt/
            // deploy-lagged) — flags it so onfinish can fall back to TTS, never silence.
            onLoadError: () => { loadFailed = true; },
            onfinish: () => {
              if (loadFailed && fallbackToken != null && !isAborted()) {
                // Via finish() so the poll is cleared and a double-settle is impossible. The value
                // matters: an abort during the fallback is reported only by resolveAbort, which does
                // not bump the generation.
                speakViaTts(fallbackToken, true).then(finish);
              } else {
                finish(false);
              }
            },
          });
        } catch (err) {
          // A synchronous throw would otherwise reject the executor with the poll still running,
          // leaking a 100ms interval for the life of the page.
          logSessionInfo('markup_sequence_play_threw', { url, message: err?.message });
          finish(false);
        }
      });
      if (aborted || isAborted()) return true;
    }
    return false;
  };

  const playUnit = (unit) => {
    if (unit.type === 'text') {
      return speakViaTts(unit.value, !!unit.skipTranslation);
    }
    if (unit.type === 'phoneme') {
      // Resolve against the story CONTENT locale so a bilingual session never leaks to the
      // English /phonemes/ set. (This player only runs for markup instructions → es-mx.)
      const urls = findFilenames(unit.value, locale) || [];
      if (!urls.length) {
        logSessionInfo('mmc_replay_phoneme_unresolved', { phoneme: unit.value });
        return Promise.resolve(false);
      }
      return playUrls(urls, null);
    }
    // syllable: pre-recorded if verified-present, else TTS (never the wrong stripped sound)
    const url = findSyllableFilename(unit.value, locale);
    return url ? playUrls([url], unit.value) : speakViaTts(unit.value, true);
  };

  for (const unit of units) {
    // Bail before the next unit if stale (unmounted/superseded) or aborted.
    if (isAborted()) return;
    // eslint-disable-next-line no-await-in-loop
    const aborted = await playUnit(unit);
    if (aborted || isAborted()) return; // abort stops the whole replay, not just this unit
  }
}
